"use strict";
// @df/api - Unified API client with authentication and error handling
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiClient = exports.apiAuthClient = exports.authClient = exports.apiClient = void 0;
const auth_1 = require("./auth");
const apiBase_1 = require("./config/apiBase");
const utils_1 = require("./utils");
// Configuration
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff
const resolveApiOriginBaseUrl = () => (0, apiBase_1.resolveApiBaseUrl)().replace(/\/api\/v1\/$/, '');
// Request counter for tracing
let requestCounter = 0;
// 429 handling - single retry with no token clearing
const SENSITIVE_LOG_PLACEHOLDER = '[REDACTED]';
const isScopedSensitiveRequest = (endpoint, requestBody) => {
    if (endpoint.includes('/invitations/scoped/validate/') ||
        endpoint.includes('/invitations/scoped/redeem/')) {
        return true;
    }
    return (endpoint.includes('/auth/users/') &&
        typeof requestBody === 'string' &&
        requestBody.includes('"scoped_invitation_token"'));
};
const redactSensitiveKeys = (value) => {
    if (Array.isArray(value)) {
        return value.map(redactSensitiveKeys);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, entryValue]) => {
            const normalizedKey = key.toLowerCase();
            if (normalizedKey.includes('token') ||
                normalizedKey.includes('authorization') ||
                normalizedKey.includes('cookie')) {
                return [key, SENSITIVE_LOG_PLACEHOLDER];
            }
            return [key, redactSensitiveKeys(entryValue)];
        }));
    }
    return value;
};
const sanitizeErrorForLogging = (error) => {
    const sanitizedError = new Error(error.message);
    sanitizedError.name = error.name;
    const original = error;
    const target = sanitizedError;
    for (const key of Object.keys(original)) {
        if (key === 'raw') {
            target[key] = SENSITIVE_LOG_PLACEHOLDER;
            continue;
        }
        target[key] = redactSensitiveKeys(original[key]);
    }
    return sanitizedError;
};
// API client class
class ApiClient {
    constructor(baseUrl, timeout = DEFAULT_TIMEOUT) {
        this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash from base
        this.timeout = timeout;
    }
    // Make HTTP request with authentication and error handling
    async request(endpoint, options = {}, retryCount = 0, isAuthRequest = false, retryOnRateLimit = true) {
        const requestId = ++requestCounter;
        // Trace auth-related requests
        if (endpoint.includes('/auth/jwt/verify') || endpoint.includes('/auth/jwt/refresh')) {
            console.log(`[Trace] auth request ${requestId} start`, {
                endpoint,
                method: options.method || 'GET',
                retryCount,
                timestamp: new Date().toISOString()
            });
        }
        const isAbsoluteUrl = /^https?:\/\//.test(endpoint);
        const isExternalUrl = isAbsoluteUrl && !endpoint.startsWith(this.baseUrl);
        // Preserve trailing slash from endpoint if present
        const url = isAbsoluteUrl ? endpoint : `${this.baseUrl}${endpoint}`;
        // Development logging for newsletter debugging
        if (process.env.NODE_ENV === 'development' && endpoint.includes('newsletter')) {
            console.log('🔍 Newsletter API Debug:');
            console.log('  Final URL:', url);
            console.log('  Method:', options.method || 'GET');
            console.log('  Body:', options.body);
            console.log('  Headers:', options.headers);
        }
        // Add authentication header (only for non-auth requests)
        const authHeader = auth_1.authManager.getAuthHeader();
        // Robust FormData detection (cross-realm & polyfills) - check the actual body that will be sent
        const requestBody = options.body;
        const isSensitiveScopedRequest = isScopedSensitiveRequest(endpoint, requestBody);
        const isFormData = typeof FormData !== 'undefined' &&
            requestBody != null &&
            (requestBody instanceof FormData ||
                Object.prototype.toString.call(requestBody) === '[object FormData]' ||
                (typeof requestBody.append === 'function' &&
                    typeof requestBody.forEach === 'function'));
        // Initialize headers object
        const headers = {};
        // Only set JSON Content-Type for non-FormData requests
        // FormData requests MUST NOT have Content-Type set manually - browser sets it with boundary
        if (!isExternalUrl && !isFormData && requestBody != null) {
            headers['Content-Type'] = 'application/json';
        }
        // [CloudUpload] Step 2: Force JSON error bodies - append Accept header
        if (!isExternalUrl) {
            headers['Accept'] = 'application/json';
        }
        // Add auth header only for non-auth requests (auth endpoints don't need Authorization header)
        if (authHeader && !isAuthRequest && !isExternalUrl) {
            headers['Authorization'] = authHeader;
        }
        // Merge user-supplied headers last (these can override defaults if explicitly set)
        if (options.headers) {
            Object.assign(headers, options.headers);
        }
        // CRITICAL: If body is FormData, remove Content-Type completely (browser will set it with boundary)
        // Do this AFTER merging user headers to ensure no caller can override this
        if (isFormData) {
            delete headers['Content-Type'];
            delete headers['content-type'];
            // Also remove any case variations that might exist
            Object.keys(headers).forEach(key => {
                if (key.toLowerCase() === 'content-type') {
                    delete headers[key];
                }
            });
        }
        // Targeted debug log for rename requests
        const willTrace = (endpoint.includes('/filesystem/files/') || endpoint.includes('/filesystem/')) &&
            (options.method === 'PATCH');
        if (willTrace) {
            // Non-PII trace of request meta
            console.debug('[RENAME][REQ]', {
                url,
                method: options.method,
                hasAuth: Boolean(headers['Authorization']),
                isFormData,
                outgoingHeaders: Object.keys(headers),
                // show whether Content-Type survived (it shouldn't)
                contentType: headers['Content-Type'] || headers['content-type'] || '(browser-set for FormData)',
                ts: new Date().toISOString(),
            });
        }
        // [apiClient] Trace request before fetch (general filesystem requests)
        const hasAuthorization = !!headers['Authorization'];
        const contentType = headers['Content-Type'] || (isFormData ? 'multipart/form-data (browser-set)' : '(not set)');
        if (endpoint.includes('/filesystem/files/') && !willTrace) {
            console.log('[apiClient] request', {
                url,
                method: options.method || 'GET',
                hasAuthorization,
                contentType,
                isBodyFormData: isFormData,
                headersKeys: Object.keys(headers),
                timestamp: new Date().toISOString()
            });
        }
        // Build final request options - ensure body and headers are correctly set
        const requestOptions = {
            method: options.method || 'GET',
            body: requestBody, // Use the body directly (FormData or stringified JSON)
            headers: headers, // Use cleaned headers (no Content-Type for FormData)
            signal: AbortSignal.timeout(this.timeout),
            credentials: 'omit', // Explicitly omit cookies for stateless JWT
            // Preserve other options like cache, redirect, etc.
            cache: options.cache,
            redirect: options.redirect,
            referrer: options.referrer,
            referrerPolicy: options.referrerPolicy,
            mode: options.mode,
        };
        try {
            const response = await fetch(url, requestOptions);
            // Trace auth responses
            if (endpoint.includes('/auth/jwt/verify') || endpoint.includes('/auth/jwt/refresh')) {
                console.log(`[Trace] auth request ${requestId} response`, {
                    status: response.status,
                    ok: response.ok,
                    retryCount,
                    timestamp: new Date().toISOString()
                });
            }
            // Handle 401 - try to refresh token once (unless this is an auth request)
            if (response.status === 401 && retryCount === 0 && !isAuthRequest) {
                console.log(`[Trace] auth request ${requestId} 401 retry`, {
                    timestamp: new Date().toISOString()
                });
                // [AUTH-AUDIT] Log 401 handler decision
                console.log('[AUTH-AUDIT] Client 401 handler calling refreshOnce');
                try {
                    await auth_1.authManager.refreshOnce();
                    console.log(`[Trace] auth request ${requestId} retry success`, {
                        timestamp: new Date().toISOString()
                    });
                    // Retry the original request once
                    return this.request(endpoint, options, retryCount + 1, isAuthRequest, retryOnRateLimit);
                }
                catch (error) {
                    console.log(`[Trace] auth request ${requestId} retry failed`, {
                        timestamp: new Date().toISOString()
                    });
                    // Refresh failed, clear tokens
                    try {
                        auth_1.authManager.clearTokens();
                    }
                    catch (clearError) {
                        console.error('[AUTH-ERROR] Failed to clear tokens after refresh failure:', clearError);
                    }
                    const authError = new Error('Authentication failed. Please log in again.');
                    authError.status = 401;
                    throw authError;
                }
            }
            // Handle 401 on auth requests (refresh/verify endpoints) - terminal failure
            if (response.status === 401 && isAuthRequest) {
                console.log(`[Trace] auth request ${requestId} 401 on auth endpoint - terminal failure`, {
                    timestamp: new Date().toISOString()
                });
                // Clear tokens and throw error - do not attempt refresh
                try {
                    auth_1.authManager.clearTokens();
                }
                catch (clearError) {
                    console.error('[AUTH-ERROR] Failed to clear tokens after auth request 401:', clearError);
                }
                const authError = new Error('Refresh token invalid. Please log in again.');
                authError.status = 401;
                throw authError;
            }
            // Handle 429 - rate limiting with single retry, no token clearing
            // (callers opt out with retryOnRateLimit=false, e.g. requests that send an email)
            if (response.status === 429 && retryCount === 0 && retryOnRateLimit) {
                if (process.env.NODE_ENV === 'development') {
                    console.log(`[Trace] auth request ${requestId} 429 retry`, {
                        timestamp: new Date().toISOString()
                    });
                }
                // [AUTH-AUDIT] Log 429 handler decision
                console.log('[AUTH-AUDIT] Client 429 handler - single retry, no token clearing');
                // Respect Retry-After header if present, otherwise use fixed 800ms delay
                const retryAfter = response.headers.get('Retry-After');
                let delay = 800; // Fixed delay
                if (retryAfter) {
                    delay = parseInt(retryAfter) * 1000; // Convert seconds to milliseconds
                }
                await this.delay(delay);
                return this.request(endpoint, options, retryCount + 1, isAuthRequest, retryOnRateLimit);
            }
            // Handle other HTTP errors
            if (!response.ok) {
                // [CloudUpload] Step 2: Force JSON error bodies and log them fully
                // Read response body once and parse it
                const text = await response.text();
                let parsedBody = null;
                try {
                    // Try to parse as JSON (DRF usually returns JSON errors)
                    parsedBody = JSON.parse(text);
                }
                catch {
                    // Not JSON, use text as-is
                    parsedBody = text;
                }
                // [CloudUpload] Step 2: Log upload error with full parsed body
                if (endpoint.includes('/filesystem/files/')) {
                    console.log('[apiClient] upload-error', {
                        url,
                        method: options.method || 'GET',
                        status: response.status,
                        statusText: response.statusText,
                        body: parsedBody
                    });
                }
                // Also keep the existing structured trace
                const bodySnippet = typeof parsedBody === 'string'
                    ? parsedBody.substring(0, 300)
                    : JSON.stringify(parsedBody).substring(0, 300);
                const errorName = 'HTTPError';
                const message = `HTTP ${response.status}: ${response.statusText}`;
                if (endpoint.includes('/filesystem/files/')) {
                    console.log(JSON.stringify({
                        trace: 'CLOUD_UPLOAD_ERROR',
                        status: response.status,
                        bodySnippet,
                        errorName,
                        message
                    }));
                }
                // Use parsed body for error object
                const data = parsedBody;
                // Development logging for newsletter 400 errors
                if (process.env.NODE_ENV === 'development' && endpoint.includes('newsletter') && response.status === 400) {
                    console.log('❌ Newsletter 400 Error Response:');
                    console.log('  Status:', response.status);
                    console.log('  Response Body:', data);
                }
                // Create enhanced error with DRF JSON
                const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
                error.status = response.status;
                error.url = `${options.method || 'GET'} ${endpoint}`;
                error.data = data;
                error.raw = text;
                // Handle 5xx server errors with user-friendly message
                if (response.status >= 500) {
                    throw new Error('Temporary server error. Please try again later.');
                }
                // Handle 4xx client errors with enhanced error
                throw error;
            }
            // Parse successful response (handle empty body for 201/204)
            const responseData = await this.parseResponse(response);
            // [CLOUD_UPLOAD_TRACE] Log structured success response for upload requests
            if (endpoint.includes('/filesystem/files/')) {
                const responseSize = responseData ? JSON.stringify(responseData).length : 0;
                console.log('[apiClient] response ok', {
                    status: response.status,
                    statusText: response.statusText,
                    hasBody: !!responseData
                });
                console.log(JSON.stringify({
                    trace: 'CLOUD_UPLOAD_RESPONSE',
                    status: response.status,
                    ok: response.ok,
                    size: responseSize,
                    serverJson: responseData
                }));
            }
            // Return parsed data, or empty object for 201/204 with empty body
            return responseData ?? {};
        }
        catch (error) {
            // [CLOUD_UPLOAD_TRACE] Log non-HTTP errors (network errors, etc.) for upload requests
            if (endpoint.includes('/filesystem/files/')) {
                const status = error?.status || null;
                const bodySnippet = '';
                const errorName = error instanceof Error ? error.name : 'UnknownError';
                const message = error instanceof Error ? error.message : 'An unexpected error occurred';
                console.log(JSON.stringify({
                    trace: 'CLOUD_UPLOAD_ERROR',
                    status,
                    bodySnippet,
                    errorName,
                    message
                }));
            }
            if (error instanceof TypeError) {
                const transportError = new Error(error.message || 'Network request failed');
                transportError.name = 'ApiTransportError';
                transportError.url = url;
                transportError.method = options.method || 'GET';
                transportError.baseUrl = this.baseUrl;
                utils_1.errorBoundary.logError(isSensitiveScopedRequest ? sanitizeErrorForLogging(transportError) : transportError, `API request to ${endpoint}`);
                throw transportError;
            }
            if (error instanceof Error) {
                utils_1.errorBoundary.logError(isSensitiveScopedRequest ? sanitizeErrorForLogging(error) : error, `API request to ${endpoint}`);
                throw error;
            }
            throw new Error('An unexpected error occurred');
        }
    }
    // Parse response based on content type (safe: handles empty bodies for 200/201/204)
    async parseResponse(response) {
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        // Handle empty body (201/204 responses may have no body)
        // Also handle 200 with content-length 0
        if (contentLength === '0') {
            return null;
        }
        // For 204 No Content, always return null (no body)
        if (response.status === 204) {
            return null;
        }
        // For 201/200, try to parse body if present
        // Read response body once (can only be read once)
        try {
            // Try to get text first (most APIs return JSON or text)
            const text = await response.text();
            // If body is empty, return null
            if (!text || text.trim().length === 0) {
                return null;
            }
            // Try to parse as JSON if content-type suggests it, or if text looks like JSON
            if (contentType && contentType.includes('application/json')) {
                try {
                    return JSON.parse(text);
                }
                catch (parseError) {
                    // If JSON parsing fails (invalid JSON), return null instead of throwing
                    // This handles malformed responses gracefully
                    return null;
                }
            }
            // Try to parse as JSON if text looks like JSON (even without content-type)
            try {
                const parsed = JSON.parse(text);
                return parsed;
            }
            catch {
                // Not JSON, continue to return as text
            }
            // For text content type or fallback, return as text
            if (contentType && contentType.includes('text/')) {
                return text;
            }
            // Default: return text if we got this far
            return text;
        }
        catch (error) {
            // If text() fails (empty body, already consumed, network error), return null
            // This prevents throwing errors for empty 201/204 responses
            // For 201/204 with no body, this is expected behavior
            return null;
        }
    }
    // Parse error response
    async parseErrorResponse(response) {
        try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }
        }
        catch {
            // Ignore parsing errors
        }
        return {
            detail: `HTTP ${response.status}: ${response.statusText}`
        };
    }
    // Utility delay function
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    // GET request
    async get(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'GET' });
    }
    // POST request
    async post(endpoint, data, options = {}) {
        const { isAuthRequest = false, retryOnRateLimit = true, ...requestInit } = options;
        return this.request(endpoint, {
            ...requestInit,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined
        }, 0, isAuthRequest, retryOnRateLimit);
    }
    // PUT request
    async put(endpoint, data, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined
        });
    }
    // PATCH request
    async patch(endpoint, data, options = {}) {
        // If data is FormData, pass it directly (don't stringify)
        // Otherwise stringify JSON objects
        const body = data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined);
        return this.request(endpoint, {
            ...options,
            method: 'PATCH',
            body
        });
    }
    // PATCH request with FormData (does not set Content-Type, lets browser set it)
    // This is the preferred method for FormData PATCH requests like rename
    async patchForm(endpoint, formData, options = {}) {
        // Explicitly pass FormData as body - request() will detect it and remove Content-Type
        // Extract headers from options but don't let it override body
        const { body: _ignoredBody, ...otherOptions } = options;
        return this.request(endpoint, {
            ...otherOptions, // Spread other options (headers, cache, etc.) but NOT body
            method: 'PATCH',
            body: formData // FormData instance - no JSON.stringify, no Content-Type header
        });
    }
    // DELETE request
    async delete(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'DELETE' });
    }
}
exports.ApiClient = ApiClient;
// Create and export API client instance
// CRA-compatible fallback: prefer PUBLIC_API_BASE, fall back to REACT_APP_API_BASE
exports.apiClient = new ApiClient((0, apiBase_1.resolveApiBaseUrl)());
// Create dedicated auth client for JWT endpoints (no /api/v1 prefix)
// AUTH_BASE resolves to server root, removing /api/v1 if present
exports.authClient = new ApiClient((0, apiBase_1.resolveAuthBaseUrl)());
// Create dedicated API-origin auth client for JWT refresh hosted on the Intermediate Server
exports.apiAuthClient = new ApiClient(resolveApiOriginBaseUrl());
